/**
 * The Strip Card Game tab (ISSUE-1288 to ISSUE-1292, ISSUE-1296,
 * ISSUE-1298), mounted by `features/poolScreen/poolTypePanes.tsx`.
 *
 * Top to bottom: the game switcher (an admin also gets Add a game and Remove
 * game), the canceled banner, the board with its rows summary, then the game
 * header and the line score. Every sheet the board opens lives here, so one
 * place owns which overlay is up.
 */
import {
  Button,
  confirmDestructive,
  InlineBanner,
  Select,
  Skeleton,
} from "@sportsmet/ui";
import { QueryClientContext } from "@tanstack/react-query";
import { type ReactNode, useContext, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  resolveDigitCellAction,
  resolveMemberCellAction,
  type typeStripRowView,
  type typeStripView,
} from "./stripBoardModel";
import { StripAddGameSheet } from "./StripAddGameSheet";
import { StripAssigneeSheet } from "./StripAssigneeSheet";
import { StripBoard } from "./StripBoard";
import {
  STRIP_ADD_GAME_LABEL,
  STRIP_CANCELED_BANNER,
  STRIP_CUSTOM_GAME_TAG,
  STRIP_GAME_CANCELED_TAG,
  STRIP_GAME_LOAD_FAILED,
  STRIP_GAME_SWITCHER_LABEL,
  STRIP_NO_GAMES,
  STRIP_REMOVE_GAME_CONFIRM_BODY,
  STRIP_REMOVE_GAME_CONFIRM_LABEL,
  STRIP_REMOVE_GAME_LABEL,
  STRIP_RENAME_GAME_LABEL,
  STRIP_TRY_AGAIN_LABEL,
  stripFilledSummary,
  stripRemoveGameConfirmTitle,
} from "./stripCardCopy";
import { StripGameHeader, StripLineScore } from "./StripGameScore";
import { StripOptionsSheet } from "./StripOptionsSheet";
import { StripRenameGameSheet } from "./StripRenameGameSheet";
import { stripShortKickoffLabel } from "./stripTime";
import { StripWinnerSheet } from "./StripWinnerSheet";
import { useStripCardGame } from "./useStripCardGame";

type typeOverlay =
  | { kind: "options"; stripNumber: number }
  | { kind: "assign"; stripNumber: number; row: number }
  | { kind: "winners"; stripNumber: number; row: number; canEdit: boolean }
  | { kind: "addGame" }
  | { kind: "renameGame" }
  | null;

export const StripCardGameTab = (props: typePoolTypePaneProps): ReactNode => {
  // The pane registry's own test mounts every pane bare, with no providers.
  // The frame still renders there; the live body needs a query client.
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return (
    <div
      className="flex flex-col gap-4"
      data-testid="strip-card-game-tab"
      data-pool-id={props.poolId}
    >
      {hasQueryClient ? <StripCardGameBody poolId={props.poolId} /> : null}
    </div>
  );
};

const StripCardGameBody = ({ poolId }: { poolId: string }): ReactNode => {
  const game = useStripCardGame(poolId);
  const [overlay, setOverlay] = useState<typeOverlay>(null);
  const timeZone = resolveViewerTimeZone();
  const closeOverlay = (): void => {
    setOverlay(null);
  };

  if (game.poolRead.isPending) {
    return (
      <div className="flex flex-col gap-3" data-testid="strip-game-loading">
        <Skeleton shape="card" />
        <Skeleton shape="card" />
      </div>
    );
  }

  const poolData = game.poolData;
  if (poolData === null) {
    return (
      <InlineBanner
        variant="warning"
        message={STRIP_GAME_LOAD_FAILED}
        action={{ label: STRIP_TRY_AGAIN_LABEL, onAction: game.refetch }}
      />
    );
  }

  const canManage = poolData.viewer.canManage;
  const leagueId = poolData.settings.leagueId;
  const selectedGame =
    game.games.find((candidate) => candidate.eventId === game.eventId) ?? null;

  const addGameSheet = canManage ? (
    <StripAddGameSheet
      open={overlay?.kind === "addGame"}
      leagueId={leagueId}
      hasLeague={poolData.settings.hasLeague !== false}
      takenEventIds={game.games.map((candidate) => candidate.eventId)}
      isBusy={game.busy?.kind === "addGame"}
      onClose={closeOverlay}
      onAdd={game.addGame}
      onAddCustom={game.addCustomGame}
    />
  ) : null;

  if (game.games.length === 0 || game.eventId === null) {
    return (
      <>
        <p
          className="text-body text-ink-secondary"
          data-testid="strip-no-games"
        >
          {STRIP_NO_GAMES}
        </p>
        {canManage ? (
          <Button
            variant="secondary"
            onClick={() => {
              setOverlay({ kind: "addGame" });
            }}
          >
            {STRIP_ADD_GAME_LABEL}
          </Button>
        ) : null}
        {addGameSheet}
      </>
    );
  }

  async function removeGame(): Promise<void> {
    if (selectedGame === null) return;
    const isConfirmed = await confirmDestructive({
      title: stripRemoveGameConfirmTitle(selectedGame.label),
      body: STRIP_REMOVE_GAME_CONFIRM_BODY,
      confirmLabel: STRIP_REMOVE_GAME_CONFIRM_LABEL,
    });
    if (!isConfirmed) return;
    await game.removeGame();
  }

  const card = game.card;
  const strips = card?.strips ?? [];
  const findStrip = (stripNumber: number): typeStripView | null =>
    strips.find((strip) => strip.stripNumber === stripNumber) ?? null;
  const findRow = (stripNumber: number, row: number): typeStripRowView | null =>
    findStrip(stripNumber)?.rows.find((candidate) => candidate.row === row) ??
    null;
  const selectedStrip =
    game.selectedStripNumber === null
      ? null
      : findStrip(game.selectedStripNumber);
  const gameLabel = selectedGame?.label ?? "";

  function onMemberCellTap(strip: typeStripView, row: typeStripRowView): void {
    if (card === null) return;
    game.selectStrip(strip.stripNumber);
    const action = resolveMemberCellAction({
      isCardCanceled: card.card.isCanceled,
      canManage: card.viewer.canManage,
      strip,
      row,
    });
    switch (action.kind) {
      case "claim":
        game.claim(strip.stripNumber, row.row);
        return;
      case "release":
        game.release(strip.stripNumber, row.row);
        return;
      case "assign":
        setOverlay({
          kind: "assign",
          stripNumber: strip.stripNumber,
          row: row.row,
        });
        return;
      case "note":
        game.showNote(action.note);
        return;
    }
  }

  function onDigitCellTap(strip: typeStripView, row: typeStripRowView): void {
    if (card === null) return;
    game.selectStrip(strip.stripNumber);
    const action = resolveDigitCellAction({
      isCardCanceled: card.card.isCanceled,
      canManage: card.viewer.canManage,
      strip,
      row,
    });
    if (action.kind === "select") return;
    setOverlay({
      kind: "winners",
      stripNumber: strip.stripNumber,
      row: row.row,
      canEdit: action.kind === "editWinners",
    });
  }

  const optionsStrip =
    overlay?.kind === "options" ? findStrip(overlay.stripNumber) : null;
  const assignStrip =
    overlay?.kind === "assign" ? findStrip(overlay.stripNumber) : null;
  const assignRow =
    overlay?.kind === "assign"
      ? findRow(overlay.stripNumber, overlay.row)
      : null;
  const winnerStrip =
    overlay?.kind === "winners" ? findStrip(overlay.stripNumber) : null;
  const winnerRow =
    overlay?.kind === "winners"
      ? findRow(overlay.stripNumber, overlay.row)
      : null;

  return (
    <>
      <div className="flex flex-col gap-2">
        <Select
          label={STRIP_GAME_SWITCHER_LABEL}
          value={game.eventId}
          items={game.games.map((candidate) => ({
            value: candidate.eventId,
            // A custom game's start is just when it was added, so it shows its tag instead.
            label: `${candidate.label}, ${
              candidate.isCustom
                ? STRIP_CUSTOM_GAME_TAG
                : stripShortKickoffLabel({
                    kickoffAt: candidate.kickoffAt,
                    timeZone,
                  })
            }${candidate.isCanceled ? `, ${STRIP_GAME_CANCELED_TAG}` : ""}`,
          }))}
          onValueChange={game.selectGame}
        />
        {canManage ? (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              data-testid="strip-add-game"
              onClick={() => {
                setOverlay({ kind: "addGame" });
              }}
            >
              {STRIP_ADD_GAME_LABEL}
            </Button>
            {selectedGame?.isCustom === true ? (
              <Button
                variant="ghost"
                data-testid="strip-rename-game"
                onClick={() => {
                  setOverlay({ kind: "renameGame" });
                }}
              >
                {STRIP_RENAME_GAME_LABEL}
              </Button>
            ) : null}
            {game.games.length > 1 ? (
              <Button
                variant="ghost"
                data-testid="strip-remove-game"
                loading={game.busy?.kind === "removeGame"}
                onClick={() => {
                  void removeGame();
                }}
              >
                {STRIP_REMOVE_GAME_LABEL}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {game.cardRead.isPending ? (
        <div className="flex flex-col gap-3" data-testid="strip-card-loading">
          <Skeleton shape="card" />
          <Skeleton shape="card" />
        </div>
      ) : card === null || selectedStrip === null ? (
        <InlineBanner
          variant="warning"
          message={STRIP_GAME_LOAD_FAILED}
          action={{ label: STRIP_TRY_AGAIN_LABEL, onAction: game.refetch }}
        />
      ) : (
        <>
          {card.card.isCanceled ? (
            <div data-testid="strip-canceled-banner">
              <InlineBanner variant="warning" message={STRIP_CANCELED_BANNER} />
            </div>
          ) : null}
          {game.note !== null ? (
            <div data-testid="strip-note">
              <InlineBanner
                variant="neutral"
                message={game.note}
                onDismiss={game.dismissNote}
              />
            </div>
          ) : null}
          <p
            className="text-caption text-ink-secondary"
            data-testid="strip-summary"
            aria-live="polite"
          >
            {`${selectedStrip.label}: ${stripFilledSummary({
              filledCount: selectedStrip.filledCount,
              rowCount: selectedStrip.rows.length,
              mySpotCount: selectedStrip.mySpotCount,
            })}`}
          </p>
          <StripBoard
            card={card}
            selectedStripNumber={selectedStrip.stripNumber}
            busy={game.busy}
            onSelectStrip={game.selectStrip}
            onMemberCellTap={onMemberCellTap}
            onDigitCellTap={onDigitCellTap}
            onManageStrip={(strip) => {
              game.selectStrip(strip.stripNumber);
              setOverlay({ kind: "options", stripNumber: strip.stripNumber });
            }}
            onAddStrip={game.addStrip}
          />
          <StripGameHeader game={card.game} />
          {card.game.isCustom ? null : (
            <StripLineScore eventId={card.game.eventId} />
          )}

          <StripOptionsSheet
            open={overlay?.kind === "options"}
            strip={optionsStrip}
            strips={strips}
            gameLabel={gameLabel}
            isCardCanceled={card.card.isCanceled}
            isBusy={
              game.busy?.kind === "lock" ||
              game.busy?.kind === "unlock" ||
              game.busy?.kind === "copy"
            }
            onClose={closeOverlay}
            onLock={game.lock}
            onUnlock={game.unlock}
            onCopy={game.copyUsers}
          />
          <StripAssigneeSheet
            open={overlay?.kind === "assign"}
            poolId={poolId}
            eventId={game.eventId}
            strip={assignStrip}
            row={assignRow}
            isBusy={game.busy?.kind === "assign"}
            onClose={closeOverlay}
            onAssign={game.assign}
          />
          <StripWinnerSheet
            open={overlay?.kind === "winners"}
            strip={winnerStrip}
            row={winnerRow}
            canEdit={overlay?.kind === "winners" && overlay.canEdit}
            isBusy={game.busy?.kind === "winners"}
            onClose={closeOverlay}
            onSave={game.setWinners}
          />
        </>
      )}
      {addGameSheet}
      {canManage && selectedGame?.isCustom === true ? (
        <StripRenameGameSheet
          open={overlay?.kind === "renameGame"}
          currentTitle={selectedGame.label}
          isBusy={game.busy?.kind === "renameGame"}
          onClose={closeOverlay}
          onSave={game.renameCustomGame}
        />
      ) : null}
    </>
  );
};
